import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "components/button";
import Icon from "components/icon";
import DateRangePicker from "components/date-range-picker";
import { Columns, TableSortState } from "components/table";
import ActionMenu from "components/shared/action-menu";
import { toast } from "react-toastify";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import useHistoryPanel from "hooks/shared/useHistoryPanel";
import { useAppTranslation } from "locale/useAppTranslation";
import { errorHandler } from "_services/errorHandler";
import { editFreezerModelPageRouteName } from "../edit/edit.page";
import {
  useFreezerModelServiceV1ListFreezerModelsQuery,
  useFreezerModelServiceV1ActivateFreezerModelsMutation,
  useFreezerModelServiceV1DeactivateFreezerModelsMutation,
} from "_services/modules/freezer_model";

export interface FreezerModelRow {
  id: string;
  freezerBrand: string;
  freezerModelName: string;
  fuelEfficiency: number;
  status: "active" | "inactive";
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

const useFreezerModelList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("freezerModel");
  const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
  const [sortState, setSortState] = useState<TableSortState>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [showToggleConfirmModal, setShowToggleConfirmModal] = useState(false);
  const [toggleRow, setToggleRow] = useState<FreezerModelRow | null>(null);
  const historyPanel = useHistoryPanel("freezer_model");

  const [activateFreezerModels] = useFreezerModelServiceV1ActivateFreezerModelsMutation();
  const [deactivateFreezerModels] = useFreezerModelServiceV1DeactivateFreezerModelsMutation();

  const { data: apiResponse, isLoading, refetch } = useFreezerModelServiceV1ListFreezerModelsQuery({
    page: currentPage,
    size: rowsPerPage,
    sortBy: sortState?.columnId || "created_at",
    sortOrder: sortState?.direction || "desc",
    status: filterValues.status || undefined,
    freezerBrand: filterValues.freezerBrand || undefined,
    freezerModelName: filterValues.freezerModelName || undefined,
    createdAtFrom: filterValues.createdAt?.split("|")[0] || undefined,
    createdAtTo: filterValues.createdAt?.split("|")[1] || undefined,
    updatedAtFrom: filterValues.updatedAt?.split("|")[0] || undefined,
    updatedAtTo: filterValues.updatedAt?.split("|")[1] || undefined,
  });

  const tableData: FreezerModelRow[] = useMemo(() => {
    if (!apiResponse?.freezer_models) return [];
    return apiResponse.freezer_models.map((item) => ({
      id: item.id ?? "",
      freezerBrand: item.freezer_brand_name ?? "",
      freezerModelName: item.freezer_model_name ?? "",
      fuelEfficiency: item.fuel_efficiency ?? 0,
      status: (item.status as "active" | "inactive") ?? "inactive",
      createdBy: item.created_by ?? "",
      createdAt: item.created_at ?? "",
      updatedBy: item.updated_by ?? "",
      updatedAt: item.updated_at ?? "",
    }));
  }, [apiResponse]);

  const totalPages = apiResponse?.metadata?.total_page ?? 1;

  useEffect(() => {
    dispatch(
      setGlobalComponent({ title: t("page-title-list"), hasBackButton: false })
    );
  }, [dispatch, t]);

  const selectedRows = tableData.filter((row) => selectedKeys.includes(row.id));
  const hasMixedStatus =
    selectedRows.length > 0 &&
    new Set(selectedRows.map((r) => r.status)).size > 1;

  const isBulkActionDisabled =
    selectedKeys.length === 0 || hasMixedStatus;

  const handleApplyBulkAction = () => {
    if (!bulkAction || isBulkActionDisabled) return;
    setShowBulkConfirmModal(true);
  };

  const handleConfirmBulkAction = async () => {
    const ids = selectedKeys.map(String);
    try {
      if (bulkAction === "activate") {
        await activateFreezerModels({ freezerModelV1FreezerModelBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      } else if (bulkAction === "deactivate") {
        await deactivateFreezerModels({ freezerModelV1FreezerModelBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      }
      setShowBulkConfirmModal(false);
      setBulkAction("");
      setSelectedKeys([]);
    } catch (error) {
      console.error("Bulk action failed:", error);
      errorHandler(error);
    }
  };

  const handleToggleStatus = (row: FreezerModelRow) => {
    setToggleRow(row);
    setShowToggleConfirmModal(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (!toggleRow) return;
    try {
      const ids = [toggleRow.id];
      if (toggleRow.status === "active") {
        await deactivateFreezerModels({ freezerModelV1FreezerModelBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      } else {
        await activateFreezerModels({ freezerModelV1FreezerModelBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      }
      setShowToggleConfirmModal(false);
      setToggleRow(null);
      refetch();
    } catch (error) {
      console.error("Toggle status failed:", error);
      errorHandler(error);
    }
  };

  const handleEdit = (row: FreezerModelRow) => {
    navigate(editFreezerModelPageRouteName(row.id.toString()));
  };

  const columns: Columns<FreezerModelRow>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no"),
        width: "60px",
      },
      {
        id: "freezerBrand",
        fieldId: "freezerBrand",
        label: t("label-freezer-brand"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "freezerModelName",
        fieldId: "freezerModelName",
        label: t("label-freezer-model-name"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "fuelEfficiency",
        fieldId: "fuelEfficiency",
        label: t("label-fuel-efficiency"),
        sortable: true,
        render: (row) => (
          <span data-testid={`col-fuel-efficiency-${row.id}`}>{row.fuelEfficiency} {t("fuel-efficiency-unit")}</span>
        ),
      },
      {
        id: "status",
        fieldId: "status",
        label: t("label-status"),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t("col-all-data"), value: "" },
          { label: t("status-active"), value: "active" },
          { label: t("status-inactive"), value: "inactive" },
        ],
        render: (row) => (
          <div className="inline-flex items-center gap-2 text-sm" data-testid={`col-status-${row.id}`}>
            <span
              className={`h-3 w-3 rounded-full ${
                row.status === "active"
                  ? "bg-primary-600 border-2 border-primary-100"
                  : "bg-greyScale-60 border-2 border-greyScale-90"
              }`}
            />
            <span className="capitalize">{row.status}</span>
          </div>
        ),
      },
      {
        id: "createdAt",
        fieldId: "createdBy",
        fieldId2: "createdAt",
        label: t("col-created-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "updatedAt",
        fieldId: "updatedBy",
        fieldId2: "updatedAt",
        label: t("col-updated-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action"),
        width: "140px",
        dragDisabled: true,
        render: (row) => (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              color="primary"
              size="small"
              iconLeft={<Icon name="edit-2" size={14} />}
              onClick={() => handleEdit(row)}
              data-testid="btn-edit-freezer"
            >
              {t("btn-edit")}
            </Button>
            <ActionMenu
              items={[
                {
                  label: row.status === "active" ? t("btn-deactivate") : t("btn-activate"),
                  onClick: () => handleToggleStatus(row),
                  variant: row.status === "active" ? "danger" : "default",
                  testId: row.status === "active" ? "btn-deactivate-freezer" : "btn-activate-freezer",
                },
                {
                  label: t("btn-track-history"),
                  onClick: () => historyPanel.openPanel(row.id, row.freezerModelName),
                  testId: "btn-track-history-freezer",
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [t],
  );

  return {
    data: tableData,
    columns,
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    selectedKeys,
    setSelectedKeys,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    navigate,
    bulkAction,
    setBulkAction,
    isBulkActionDisabled,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    showToggleConfirmModal,
    setShowToggleConfirmModal,
    toggleRow,
    handleApplyBulkAction,
    handleConfirmBulkAction,
    handleConfirmToggleStatus,
    historyPanel,
    isLoading,
  };
};

export default useFreezerModelList;
